import React, { useState, useMemo } from 'react';
import { useDb } from '../../db/useDataService';

import {
  PlusOutlined,
  CheckOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  Modal,
  Tabs,
  Form,
  Input,
  Select,
  Radio,
  InputNumber,
  Checkbox,
  Typography,
  Space,
  Tag,
  Button,
  Divider,
  Tree,
  Popover,
  message,
} from 'antd';

import {
  Campaign,
  AdGroup,
  CampaignAd,
  CampaignPlacement,
  CampaignPaymentModel,
  GroupingLevel,
  AdGroupType,
  AD_GROUP_TYPE_CONFIG,
  AngleType,
  ANGLE_TYPE_CONFIG,
  GROUPING_LEVEL_CONFIG,
  AdPlatform,
  LifeSphere,
  SmOffer,
  AdCabinet,
  AdSitelink,
} from '../../types';

const { Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface CreateCampaignModalProps {
  visible: boolean;
  onClose: () => void;
  onCreate: (campaigns: Campaign[], groups: AdGroup[], ads: CampaignAd[]) => void;
  productId: string;
  spheres: LifeSphere[];
  offers: SmOffer[];
  cabinets: AdCabinet[];
}

let modalNextId = 10000;
const nextId = () => String(modalNextId++);

const emptySitelink: AdSitelink = { title: '', description: '', url: '' };
const emptySitelinks: [AdSitelink, AdSitelink, AdSitelink, AdSitelink] = [emptySitelink, emptySitelink, emptySitelink, emptySitelink];

const CreateCampaignModal: React.FC<CreateCampaignModalProps> = ({
  visible,
  onClose,
  onCreate,
  productId,
  spheres,
  offers,
  cabinets,
}) => {
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('settings');

  // Architecture state
  const [groupingLevel, setGroupingLevel] = useState<GroupingLevel>('subtheme');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [groupsPerCampaign, setGroupsPerCampaign] = useState<number | null>(null);
  const [groupTypes, setGroupTypes] = useState<AdGroupType[]>(['keywords']);
  const [angleCounts, setAngleCounts] = useState<Partial<Record<AngleType, number>>>({ pain: 1, instruction: 1 });
  const selectedAngles = useMemo(() => (Object.keys(angleCounts) as AngleType[]).filter((a) => (angleCounts[a] || 0) > 0), [angleCounts]);
  const adsPerGroup = useMemo(() => selectedAngles.reduce((sum, a) => sum + (angleCounts[a] || 0), 0), [angleCounts, selectedAngles]);

  // Статистика по стилям из БД.
  const db = useDb();
  const activeAdsByAngle = useMemo(() => {
    const productCampaignIds = new Set(db.campaigns.filter((c) => c.productId === productId).map((c) => c.id));
    const map: Partial<Record<AngleType, number>> = {};
    db.campaignAds.forEach((a) => {
      if (!productCampaignIds.has(a.campaignId)) return;
      if (a.status !== 'active') return;
      map[a.angleType] = (map[a.angleType] || 0) + 1;
    });
    return map;
  }, [db.campaigns, db.campaignAds, productId]);

  // ROMI стабильный по типу (заглушка, пока нет реальной метрики).
  const angleRomi = useMemo(() => {
    const map: Partial<Record<AngleType, number>> = {};
    (Object.keys(ANGLE_TYPE_CONFIG) as AngleType[]).forEach((t) => {
      let hash = 0;
      for (let i = 0; i < t.length; i++) hash = ((hash << 5) - hash + t.charCodeAt(i)) | 0;
      map[t] = 60 + Math.abs(hash) % 180;
    });
    return map;
  }, []);

  const yaCabinets = useMemo(() => cabinets.filter((c) => c.platform === 'yandex_direct'), [cabinets]);

  // Build tree data based on grouping level
  const treeData = useMemo(() => {
    if (groupingLevel === 'sphere') {
      return spheres.map((s) => ({
        title: s.name,
        key: s.id,
      }));
    }
    if (groupingLevel === 'theme') {
      return spheres.map((s) => ({
        title: s.name,
        key: `s_${s.id}`,
        selectable: false,
        children: s.themes.map((c) => ({
          title: c.name,
          key: c.id,
        })),
      }));
    }
    // theme
    return spheres.map((s) => ({
      title: s.name,
      key: `s_${s.id}`,
      selectable: false,
      children: s.themes.map((c) => ({
        title: c.name,
        key: `c_${c.id}`,
        selectable: false,
        children: c.subthemes.map((t) => ({
          title: `${t.name} (${t.units.length} СЕ)`,
          key: t.id,
        })),
      })),
    }));
  }, [spheres, groupingLevel]);

  // Preview: what groups will be created
  const previewGroups = useMemo(() => {
    const groups: Array<{ id: string; name: string; unitsCount: number; keywordsCount: number }> = [];

    selectedItemIds.forEach((itemId) => {
      if (groupingLevel === 'subtheme') {
        for (const s of spheres) {
          for (const c of s.themes) {
            const t = c.subthemes.find((th) => th.id === itemId);
            if (t) {
              groups.push({
                id: itemId,
                name: t.name,
                unitsCount: t.units.length,
                keywordsCount: (t.keywords || []).length + t.units.reduce((acc, u) => acc + (u.keywords || []).length, 0),
              });
            }
          }
        }
      } else if (groupingLevel === 'theme') {
        for (const s of spheres) {
          const c = s.themes.find((cat) => cat.id === itemId);
          if (c) {
            groups.push({
              id: itemId,
              name: c.name,
              unitsCount: c.subthemes.reduce((acc, t) => acc + t.units.length, 0),
              keywordsCount: c.subthemes.reduce((acc, t) => acc + (t.keywords || []).length, 0),
            });
          }
        }
      } else {
        const s = spheres.find((sp) => sp.id === itemId);
        if (s) {
          groups.push({
            id: itemId,
            name: s.name,
            unitsCount: s.themes.reduce((acc, c) => acc + c.subthemes.reduce((a2, t) => a2 + t.units.length, 0), 0),
            keywordsCount: s.themes.reduce((acc, c) => acc + c.subthemes.reduce((a2, t) => a2 + (t.keywords || []).length, 0), 0),
          });
        }
      }
    });

    return groups;
  }, [selectedItemIds, groupingLevel, spheres]);

  const effectiveGroupsPerCampaign = groupsPerCampaign && groupsPerCampaign > 0
    ? Math.min(groupsPerCampaign, Math.max(previewGroups.length, 1))
    : Math.max(previewGroups.length, 1);
  const campaignsCount = previewGroups.length === 0
    ? 0
    : Math.ceil(previewGroups.length / effectiveGroupsPerCampaign);
  const totalAds = previewGroups.length * adsPerGroup;

  const handleCreate = () => {
    form.validateFields().then((values) => {
      const campaigns: Campaign[] = [];
      const groups: AdGroup[] = [];
      const ads: CampaignAd[] = [];

      // Шардим группы по campaignsCount кампаниям.
      const chunks: typeof previewGroups[] = [];
      for (let i = 0; i < campaignsCount; i++) {
        chunks.push(previewGroups.slice(i * effectiveGroupsPerCampaign, (i + 1) * effectiveGroupsPerCampaign));
      }

      chunks.forEach((chunk, idx) => {
        const campaignId = nextId();
        const baseName: string = values.name;
        campaigns.push({
          id: campaignId,
          productId,
          name: chunks.length > 1 ? `${baseName} — ${idx + 1}` : baseName,
          platform: 'yandex_direct',
          cabinetId: values.cabinetId,
          offerId: values.offerId,
          placement: values.placement || 'search',
          strategy: 'max_conversions',
          paymentModel: values.paymentModel || 'per_conversions',
          weeklyBudget: values.weeklyBudget || 0,
          conversionPriceLimit: values.conversionPriceLimit || null,
          conversionName: values.conversionName || '',
          negativeKeywords: (values.negativeKeywords || '').split('\n').map((s: string) => s.trim()).filter(Boolean),
          settings: {
            groupingLevel,
            selectedItemIds: chunk.map((g) => g.id),
            adsPerGroup,
            selectedAngles,
            groupTypes: groupTypes.length > 0 ? groupTypes : ['keywords'],
          },
          status: 'draft',
          createdAt: new Date().toISOString(),
          createdBy: 'user1',
          clonedFromId: null,
        });

        chunk.forEach((pg) => {
          // Для каждого PG создаём по одной группе на каждый выбранный тип группы.
          const activeGroupTypes: AdGroupType[] = groupTypes.length > 0 ? groupTypes : ['keywords'];
          activeGroupTypes.forEach((gType) => {
            const groupId = nextId();
            const typeLabel = AD_GROUP_TYPE_CONFIG[gType];
            groups.push({
              id: groupId,
              campaignId,
              sourceId: pg.id,
              sourceLevel: groupingLevel,
              name: activeGroupTypes.length > 1 ? `${pg.name} — ${typeLabel}` : pg.name,
              keywords: gType === 'keywords' ? [pg.name.toLowerCase()] : [],
              suKeywords: [],
              images: [],
              enabled: true,
              type: gType,
            });

            // Разворачиваем angleCounts в плоский список: N объявлений стиля A, M стиля B и т.д.
            const angleQueue: AngleType[] = [];
            selectedAngles.forEach((a) => {
              const n = angleCounts[a] || 0;
              for (let k = 0; k < n; k++) angleQueue.push(a);
            });
            for (let i = 0; i < angleQueue.length; i++) {
              const angle = angleQueue[i];
              const angleCfg = ANGLE_TYPE_CONFIG[angle];
              ads.push({
                id: nextId(),
                adGroupId: groupId,
                campaignId,
                unitId: '',
                angleType: angle,
                title: `${pg.name} — ${angleCfg.label}`,
                title2: 'Бесплатный вебинар',
                text: `Объявление в стиле «${angleCfg.label}» для группы «${pg.name}» (${typeLabel}).`,
                url: 'example.com',
                displayUrl: '',
                sitelinks: emptySitelinks,
                callouts: [],
                actionButton: '',
                image: undefined,
                imageText: '',
                imageButtonText: '',
                status: 'draft',
                variantIndex: i + 1,
              });
            }
          });
        });
      });

      onCreate(campaigns, groups, ads);
      form.resetFields();
      setSelectedItemIds([]);
      setActiveTab('settings');
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedItemIds([]);
    setActiveTab('settings');
    onClose();
  };

  const angleTypes = Object.keys(ANGLE_TYPE_CONFIG) as AngleType[];

  return (
    <Modal
      title="Создать кампанию"
      visible={visible}
      onCancel={handleCancel}
      width={1080}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={handleCancel}>Отмена</Button>,
        <Button
          key="create"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleCreate}
          disabled={selectedItemIds.length === 0}
          style={{ backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }}
        >
          Создать ({campaignsCount} камп., {previewGroups.length} групп, {totalAds} объявл.)
        </Button>,
      ]}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Настройки" key="settings">
          <Form form={form} layout="vertical" requiredMark={false} initialValues={{ placement: 'search', paymentModel: 'per_conversions', weeklyBudget: 35000 }}>
            <Form.Item name="name" label="Название кампании" rules={[{ required: true, message: 'Введите название' }]}>
              <Input placeholder="Например: Похудение — Поиск" />
            </Form.Item>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item name="cabinetId" label="Кабинет" rules={[{ required: true, message: 'Выберите кабинет' }]}>
                <Select placeholder="Выберите кабинет">
                  {yaCabinets.map((c) => (
                    <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item name="offerId" label="Оффер" rules={[{ required: true, message: 'Выберите оффер' }]}>
                <Select placeholder="Выберите оффер">
                  {offers.map((o) => (
                    <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item name="placement" label="Размещение">
                <Radio.Group>
                  <Radio.Button value="search">Поиск</Radio.Button>
                  <Radio.Button value="networks">Сети</Radio.Button>
                  <Radio.Button value="both">Оба</Radio.Button>
                </Radio.Group>
              </Form.Item>

              <Form.Item name="paymentModel" label="Оплата">
                <Radio.Group>
                  <Radio.Button value="per_conversions">За конверсии</Radio.Button>
                  <Radio.Button value="per_clicks">За клики</Radio.Button>
                </Radio.Group>
              </Form.Item>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <Form.Item name="weeklyBudget" label="Бюджет/нед. (₽)">
                <InputNumber style={{ width: '100%' }} min={0} step={1000} />
              </Form.Item>

              <Form.Item name="conversionPriceLimit" label="Цена конверсии (₽)">
                <InputNumber style={{ width: '100%' }} min={0} step={100} placeholder="Без огран." />
              </Form.Item>

              <Form.Item name="conversionName" label="Конверсия (Метрика)">
                <Input placeholder="lead_webinar" />
              </Form.Item>
            </div>

            <Form.Item name="negativeKeywords" label="Минус-фразы" extra="Каждая фраза с новой строки">
              <TextArea rows={3} placeholder={'бесплатно\nскачать\nреферат'} />
            </Form.Item>
          </Form>
        </TabPane>

        <TabPane tab="Архитектура" key="architecture">
          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ marginRight: 12 }}>Группа объявлений по:</Text>
            <Radio.Group
              value={groupingLevel}
              onChange={(e) => { setGroupingLevel(e.target.value); setSelectedItemIds([]); }}
            >
              <Radio.Button value="subtheme">Подтемам</Radio.Button>
              <Radio.Button value="theme">Темам</Radio.Button>
              <Radio.Button value="sphere">Сферам</Radio.Button>
            </Radio.Group>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong style={{ marginRight: 12 }}>Типы групп в кампании:</Text>
            <Checkbox.Group
              value={groupTypes}
              onChange={(vals) => setGroupTypes(vals as AdGroupType[])}
              options={(Object.keys(AD_GROUP_TYPE_CONFIG) as AdGroupType[]).map((k) => ({
                label: AD_GROUP_TYPE_CONFIG[k],
                value: k,
              }))}
            />
            <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
              Каждая выбранная опция создаёт отдельную группу того же узла иерархии.
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Выберите элементы:</Text>
            <div style={{ marginTop: 8, maxHeight: 420, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 8, padding: 8 }}>
              <Tree
                checkable
                treeData={treeData}
                checkedKeys={selectedItemIds}
                onCheck={(keys) => setSelectedItemIds(keys as string[])}
                defaultExpandAll
              />
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Групп объявлений в кампании:</Text>
            <InputNumber
              value={groupsPerCampaign ?? undefined}
              onChange={(v) => setGroupsPerCampaign(v || null)}
              min={1}
              max={previewGroups.length || 1}
              placeholder={`все (${previewGroups.length || 0})`}
              style={{ width: '100%', marginTop: 4 }}
            />
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
              Если меньше общего числа групп — будет создано несколько кампаний.
            </Text>
          </div>

          <div style={{ marginBottom: 16 }}>
            <Text strong>Стили объявлений:</Text>
            <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 8, marginTop: 2 }}>
              Отметьте стили и задайте, сколько объявлений каждого стиля генерировать в группу. Оптимально 3–5 объявлений в группе суммарно, больше 7 — бессмысленно.
            </Text>

            {/* Header */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '28px 1fr 24px 70px 60px 70px',
              gap: 8,
              padding: '6px 10px',
              background: '#fafafa',
              borderRadius: '6px 6px 0 0',
              border: '1px solid #f0f0f0',
              borderBottom: 'none',
              fontSize: 11,
              fontWeight: 600,
              color: '#8c8c8c',
            }}>
              <span />
              <span>Стиль</span>
              <span />
              <span style={{ textAlign: 'center' }}>Активных</span>
              <span style={{ textAlign: 'center' }}>ROMI</span>
              <span style={{ textAlign: 'center' }}>К-во</span>
            </div>

            <div style={{
              border: '1px solid #f0f0f0',
              borderRadius: '0 0 6px 6px',
              maxHeight: 340,
              overflowY: 'auto',
            }}>
              {angleTypes.map((type, idx) => {
                const cfg = ANGLE_TYPE_CONFIG[type];
                const count = angleCounts[type] || 0;
                const isSelected = count > 0;
                const activeCount = activeAdsByAngle[type] || 0;
                const romi = angleRomi[type] || 0;
                const toggle = () => {
                  setAngleCounts((prev) => {
                    const next = { ...prev };
                    if (next[type]) delete next[type];
                    else next[type] = 1;
                    return next;
                  });
                };
                const setCount = (n: number | null) => {
                  setAngleCounts((prev) => {
                    const next = { ...prev };
                    if (!n || n <= 0) delete next[type];
                    else next[type] = n;
                    return next;
                  });
                };
                const romiColor = romi >= 150 ? '#52c41a' : romi >= 100 ? '#faad14' : '#ff4d4f';

                const infoContent = (
                  <div style={{ maxWidth: 360, fontSize: 12, lineHeight: 1.55 }}>
                    <div style={{ marginBottom: 8 }}>
                      <strong>{cfg.label}</strong>
                    </div>
                    <div style={{ marginBottom: 6 }}>{cfg.description}</div>
                    <div style={{
                      padding: 6,
                      background: '#fafafa',
                      borderLeft: '3px solid #1890ff',
                      marginBottom: 8,
                      fontStyle: 'italic',
                    }}>
                      <div style={{ fontSize: 10, color: '#8c8c8c', marginBottom: 2 }}>Пример:</div>
                      {cfg.example}
                    </div>
                    {cfg.howItWorks && (
                      <div style={{ marginBottom: 6 }}>
                        <strong>Как работает:</strong> {cfg.howItWorks}
                      </div>
                    )}
                    {cfg.whenToUse && (
                      <div style={{ marginBottom: 6, color: '#389e0d' }}>
                        <strong>Когда использовать:</strong> {cfg.whenToUse}
                      </div>
                    )}
                    {cfg.whenNotToUse && (
                      <div style={{ color: '#cf1322' }}>
                        <strong>Когда не стоит:</strong> {cfg.whenNotToUse}
                      </div>
                    )}
                  </div>
                );

                return (
                  <div
                    key={type}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '28px 1fr 24px 70px 60px 70px',
                      gap: 8,
                      alignItems: 'center',
                      padding: '8px 10px',
                      background: isSelected ? '#e6f7ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
                      borderBottom: idx === angleTypes.length - 1 ? 'none' : '1px solid #f0f0f0',
                      fontSize: 12,
                    }}
                  >
                    <Checkbox checked={isSelected} onChange={toggle} />
                    <div onClick={toggle} style={{ cursor: 'pointer', minWidth: 0 }}>
                      <div style={{ fontWeight: 500 }}>{cfg.label}</div>
                      <div style={{ color: '#8c8c8c', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cfg.description}
                      </div>
                      <div style={{ color: '#595959', fontSize: 10, fontStyle: 'italic', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                        «{cfg.example}»
                      </div>
                    </div>
                    <Popover content={infoContent} trigger="click" placement="left">
                      <InfoCircleOutlined style={{ color: '#1890ff', cursor: 'pointer', fontSize: 14 }} />
                    </Popover>
                    <span style={{ textAlign: 'center', color: activeCount > 0 ? '#262626' : '#bfbfbf' }}>
                      {activeCount}
                    </span>
                    <span style={{ textAlign: 'center', color: activeCount > 0 ? romiColor : '#bfbfbf', fontWeight: activeCount > 0 ? 600 : 400 }}>
                      {activeCount > 0 ? `${romi}%` : '—'}
                    </span>
                    <div style={{ textAlign: 'center' }}>
                      {isSelected ? (
                        <InputNumber
                          size="small"
                          value={count}
                          onChange={(v) => setCount(typeof v === 'number' ? v : null)}
                          min={1}
                          max={7}
                          style={{ width: 56 }}
                        />
                      ) : (
                        <span style={{ color: '#bfbfbf' }}>—</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* Preview */}
          <div style={{ background: '#fafafa', borderRadius: 8, padding: 12 }}>
            <Text strong>Предпросмотр:</Text>
            <div style={{ marginTop: 8, fontSize: 13 }}>
              <div>Кампаний: <strong>{campaignsCount}</strong></div>
              <div>Групп всего: <strong>{previewGroups.length}</strong></div>
              <div>Групп в одной кампании: <strong>{effectiveGroupsPerCampaign}</strong></div>
              <div>Объявлений на группу: <strong>{adsPerGroup}</strong></div>
              <div>Стилей: <strong>{selectedAngles.length}</strong></div>
              <div>Итого объявлений: <strong>{totalAds}</strong></div>
            </div>
            {previewGroups.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {previewGroups.map((g) => (
                  <Tag key={g.id} style={{ marginBottom: 4 }}>{g.name} ({g.unitsCount} СЕ)</Tag>
                ))}
              </div>
            )}
          </div>
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default CreateCampaignModal;
